import { InertiaPage } from "@/types/inertia";
import { Icon } from "@iconify/react/dist/iconify.js";
import { router, useForm, usePage } from "@inertiajs/react";
import axios from "axios";
import { ChangeEvent, useEffect, useState } from "react";

interface FileDisplayProps {
    file: string;
    currentDirectory: string;
}

interface DirectoryInterface {
    [key: number]: string
}

const MediaContent = ({ setData, fileKey, closeLibrary, disk, acceptedFormats = 'image/*,.docx,.pdf,.odt,.xls,.xlsx,.mp4,.mp3,.mov,.webm,.avi' }: {
    setData: (key: string, value: any) => void,
    fileKey: string;
    closeLibrary: () => void;
    disk: string;
    acceptedFormats?: string,
}) => {
    const [directories, setDirectories] = useState<DirectoryInterface[]>([]);
    const [files, setFiles] = useState<string[]>([]);
    const [canSubmit, setCanSubmit] = useState<boolean>(false);
    const [sent, setSent] = useState<boolean>(false);

    const returnedFile = usePage<InertiaPage>().props.flash.file;

    const { data: uploadedData, setData: setUploadedData, post, progress, reset, processing } = useForm<{ uploadedFile: any }>({
        uploadedFile: null,
    })

    const mimeTypesMap: { [key: string]: string } = {
        'application/pdf': '.pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
        'application/vnd.oasis.opendocument.text': '.odt',
        'application/vnd.ms-excel': '.xls',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
        'video/mp4': '.mp4',
        'audio/mpeg': '.mp3',
        'video/quicktime': '.mov',
        'video/webm': '.webm',
        'video/x-msvideo': '.avi',
        'image/jpeg': '.jpg',
        'image/png': '.png',
        'image/gif': '.gif',
        'image/svg+xml': '.svg',
        'image/webp': '.webp',
    };

    useEffect(() => {
        if (returnedFile !== null) {
            reset()
            fetchContentsFromDirectory()
        }
    }, [returnedFile])


    useEffect(() => {
        const acceptedFormatsArray = acceptedFormats.split(',');

        if (processing || uploadedData.uploadedFile !== null) {
            setCanSubmit(true);
        }

        if (uploadedData.uploadedFile) {
            const file = uploadedData.uploadedFile;
            const fileType = file.type;

            const fileExtension = mimeTypesMap[fileType] || '';

            const isValidFormat = acceptedFormatsArray.some(format => {
                if (format.includes('*')) {
                    return fileExtension.startsWith(format.split('/')[0]);
                }

                return format === fileExtension;
            });

            setCanSubmit(isValidFormat);
        }

    }, [uploadedData, processing, returnedFile])

    const UploadNewFileArea = () => {
        const [droppable, setDroppable] = useState<boolean>(false);
        const [error, setError] = useState<boolean>(false);

        const handleSubmitFile = () => {
            post(route('system.mediaLibrary.uploadFile', { disk: disk }));
        }

        return (
            <div className="px-4">
                <p className="h5 text-arcticMist font-semibold mb-6">Dodaj nowy załącznik</p>
                <div className={`mb-8 mt-1 border-royalPeriwinkle cursor-pointer border-2 px-6 py-16 border-dashed relative  ${droppable && 'animate-scale'}`} onDrop={() => setDroppable(false)} onDragLeave={() => setDroppable(false)} onDragEnter={() => setDroppable(true)}>
                    <input accept={acceptedFormats} name='uploadedFile' type="file" onInput={(e: any) => setUploadedData('uploadedFile', e.target.files[0])} className='opacity-0 cursor-pointer absolute h-full w-full top-0 left-0' />
                    <label htmlFor='uploadedFile' className='text-center block h5 text-accordorange cursor-pointer break-all'>
                        {!uploadedData.uploadedFile ? 'Dodaj nowy załącznik' : uploadedData.uploadedFile.name}
                    </label>
                </div>
                {error &&
                    <p className="text-crimsonAlert">Wystąpił błąd podczas dodawania załącznika, proszę spróbować ponownie</p>
                }
                <div className="h-2 w-full bg-royalPeriwinkle rounded-full my-6">
                    <div
                        className="h-full bg-accordorange rounded-full"
                        style={{ width: `${progress?.percentage ? progress?.percentage : 0}%` }}
                    ></div>
                    <p className="text-14 leading-18 text-arcticMist mt-2 text-right">{progress?.percentage ? `${progress?.percentage}%` : ''}</p>
                </div>
                <p className="text-arcticMist text-12 break-words">Obsługiwane rozszerzenia: {acceptedFormats}</p>
                <button type="button" disabled={!canSubmit && !sent} className="main-btn mt-6" onClick={handleSubmitFile}>Zapisz</button>
            </div>
        )
    }

    const FileDisplay = ({ file }: FileDisplayProps) => {
        const fileExtension = file.split('.').pop();

        if (fileExtension === 'pdf') {
            return (
                <>
                    <Icon icon={'fa6-regular:file-pdf'} width={100} className="text-arcticMist mx-auto mb-4" />
                    <p className="break-all text-arcticMist text-14 leading-18">{file}</p>
                </>
            );
        }

        if (fileExtension === 'docx' || fileExtension === 'doc') {
            return (
                <>
                    <Icon icon={'tabler:file-type-docx'} width={100} className="text-arcticMist mx-auto mb-4" />
                    <p className="break-all text-arcticMist text-14 leading-18">{file}</p>
                </>
            );
        }

        if (fileExtension === 'xls' || fileExtension === 'xlsx') {
            return (
                <>
                    <Icon icon={'tabler:file-type-xls'} width={100} className="text-arcticMist mx-auto mb-4" />
                    <p className="break-all text-arcticMist text-14 leading-18">{file}</p>
                </>
            );
        }

        if (fileExtension === 'mp4' || fileExtension === 'mov' || fileExtension === 'webm') {
            return (
                <>
                    <Icon icon={'bx:camera-movie'} width={100} className="text-arcticMist mx-auto mb-4" />
                    <p className="break-all text-arcticMist text-14 leading-18">{file}</p>
                </>
            );
        }

        if (fileExtension === 'mp3') {
            return (
                <>
                    <Icon icon={'teenyicons:mp3-outline'} width={100} className="text-arcticMist mx-auto mb-4" />
                    <p className="break-all text-arcticMist text-14 leading-18">{file}</p>
                </>
            );
        }

        if (typeof fileExtension === 'string' && ['jpg', 'jpeg', 'png', 'gif', 'svg'].includes(fileExtension)) {
            const imageUrl = `/files/${disk}/${file}`;
            return (
                <>
                    <img className="w-full h-full object-cover" src={imageUrl} alt={file} />
                    <p className="break-all text-arcticMist text-14 leading-18 my-4">{file}</p>
                </>
            )
        }

        return (
            <>
                <div className="relative">
                    <Icon icon="mdi:file" width={100} className="text-arcticMist mx-auto mb-4"/>
                    <p className="uppercase font-bold tracking-wider absolute left-[86px] bottom-10">{(/(?:\.([^.]+))?$/.exec(file) || [null])[1] || '.'}</p>
                </div>
                <p className="break-all text-arcticMist text-14 leading-18 my-4">{file}</p>
            </>
        )
    }

    useEffect(() => {
        const fetchFiles = async () => {
            setDirectories([])

            const newData = await axios.get(route('system.mediaLibrary.getContentsFromDirectory', { directory: disk }))

            setDirectories(newData.data.directories);
            setFiles(newData.data.files);
        }

        fetchFiles();
    }, [])

    const setFile = (file: string) => {
        const fullFileName = `/files/${disk}/${file}`;
        setData(fileKey, fullFileName);

        closeLibrary();
    }

    const fetchContentsFromDirectory = async () => {
        const fetchFiles = async () => {
            setDirectories([])

            const newData = await axios.get(route('system.mediaLibrary.getContentsFromDirectory', { directory: disk }))

            setDirectories(newData.data.directories);
            setFiles(newData.data.files);
        }

        fetchFiles();
    }

    const deleteFile = async (file: string) => {
        const formData = new FormData();

        formData.append('fileToDelete', file);
        formData.append('disk', disk);

        setData(fileKey, '');

        await axios.post(route('system.mediaLibrary.deleteFile'), formData);

        fetchContentsFromDirectory()
    }

    return (
        <div className="flex gap-x-6 h-full">
            <div className="w-9/12 overflow-y-scroll no-scrollbar">
                <p className="h4 text-arcticMist font-semibold mb-6">Biblioteka mediów, wybrany dysk: <i className="text-accordorange">{disk}</i></p>
                <div className="grid grid-cols-5 gap-x-6 gap-y-10">
                    {files.length > 0 && files.map((file, i) => (
                        <div key={file + i} className="w-full grid items-start">
                            <button type="button" onClick={() => setFile(file)} className={`flex flex-col w-full disabled:opacity-30 h-auto`} disabled={processing}>
                                <FileDisplay currentDirectory={disk} file={file} />
                            </button>
                            <div className="mt-auto">
                                <button type="button" className="block red-btn mt-auto w-full" onClick={() => deleteFile(file)}>
                                    Usuń
                                </button>
                                <a href={route('files.privateFile', { disk: disk, file: file })} target="_blank" className="block border-btn mt-4 w-full text-center">
                                    Podgląd
                                </a>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <div className="border-1 border-royalPeriwinkle"></div>
            <div className="w-3/12 overflow-y-scroll">
                <UploadNewFileArea />
            </div>
        </div>
    )
}

export default MediaContent;